<template>
  <div class="row">
    <div class="col-12">
      <card>
        <tab-rail v-model="activeTab" :tabs="tabs" class="mb-3"></tab-rail>

        <filter-bar
          :filters="filterSchema"
          :initial-value="filterValues"
          :result-count="totalRows"
          :loading="loading"
          :per-page="perPage"
          :per-page-options="perPageOptions"
          saved-filters-key="booking-requests"
          :extra-saved-state="{ tab: activeTab }"
          @change="onFiltersChanged"
          @update:perPage="onPerPageChange"
          @restore-extra="onRestoreExtra"
        >
          <template #actions>
            <base-button v-if="hasPermissions('download_data')" icon class="btn-link text-white" @click="downloadCSV()">
              <i class="tim-icons icon-cloud-download-93"></i>
            </base-button>
          </template>
        </filter-bar>

        <div v-if="loading && requests.length === 0" class="text-center text-muted py-5">
          <i class="el-icon-loading mr-2"></i> Loading requests...
        </div>

        <div v-else-if="requests.length === 0" class="text-center py-5">
          <img src="/img/pandas/empty-requests.png" class="empty-state-img" :alt="activeTab === 0 ? 'No pending requests' : 'No decided requests yet'" />
          <p class="mb-1 text-white">{{ activeTab === 0 ? 'No pending requests' : 'No decided requests yet' }}</p>
          <small v-if="activeTab === 0" class="text-white">Requests appear here when customers submit a booking request from your booking site.</small>
        </div>

        <div v-else class="table-responsive">
          <table class="table tablesorter">
            <thead class="text-primary">
              <tr>
                <th>Received</th>
                <th>Customer</th>
                <th>Event</th>
                <th>Requested time</th>
                <th>Payment</th>
                <th v-if="activeTab === 0">Expires</th>
                <th v-else>Outcome</th>
                <th class="text-right" v-if="activeTab === 0">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in requests" :key="'BR' + r.id">
                <td style="white-space: nowrap">{{ getLocalDateTime(r.created_at) }}</td>
                <td>
                  <a v-if="r.end_user_id" class="text-white request-row-link" target="_blank" :href="'/customers/details?id=' + r.end_user_id">{{ customerName(r) }}<i class="tim-icons el-icon-top-right request-row-link__arrow"></i></a>
                  <div v-else class="text-white">{{ customerName(r) }}</div>
                  <small v-if="r.customer_email" class="text-white d-block">
                    <i class="tim-icons icon-email-85 mr-1"></i>{{ r.customer_email }}
                  </small>
                  <small v-if="r.customer_phone" class="text-white d-block">
                    <i class="tim-icons icon-mobile mr-1"></i>{{ formatPhone(r.customer_phone, r.customer_country) }}
                  </small>
                </td>
                <td>
                  <div class="text-white">{{ eventName(r) }}</div>
                  <badge v-if="r.mode === 'form'" type="info">Intake form</badge>
                </td>
                <td style="white-space: nowrap">
                  <span v-if="r.booking_date" class="text-white">{{ getDate(r.booking_date, date_format) }}, {{ getTime(r.start_time, time_format) }}</span>
                  <span v-else-if="r.requested_date" class="text-white">{{ getDate(r.requested_date, date_format) }}<span v-if="r.requested_time">, {{ getTime(r.requested_time, time_format) }}</span></span>
                  <span v-else class="text-white">Flexible</span>
                </td>
                <td>
                  <a v-if="r.transaction_id" class="text-white request-row-link" target="_blank" :href="'/purchases/transactions/details?id=' + r.transaction_id">#{{ r.order_number }}<i class="tim-icons el-icon-top-right request-row-link__arrow"></i></a>
                  <small v-if="r.transaction_id" class="text-white d-block">{{ getAmount(r.paid || 0) }} paid &middot; {{ getAmount(r.due || 0) }} due</small>
                  <small class="text-white d-block">{{ r.payment_capture_mode === 'charge_now' ? 'Charged at request' : 'Charged after approval' }}</small>
                </td>
                <td v-if="activeTab === 0" style="white-space: nowrap">
                  <span v-if="r.expires_at" class="text-white">{{ getLocalDateTime(r.expires_at) }}</span>
                  <span v-else class="text-white">Never</span>
                </td>
                <td v-else>
                  <badge :type="outcomeBadgeType(r.status)">{{ outcomeLabel(r.status) }}</badge>
                  <div v-if="r.decided_by_first_name" class="text-white" style="font-size: 0.75rem">
                    by {{ r.decided_by_first_name }} {{ r.decided_by_last_name }}
                  </div>
                  <div v-if="r.deny_reason" class="text-white" style="font-size: 0.75rem">{{ r.deny_reason }}</div>
                </td>
                <td class="text-right" v-if="activeTab === 0" style="white-space: nowrap">
                  <base-button
                    size="sm"
                    variant="outlined"
                    tone="primary"
                    :disabled="actingOn === r.id"
                    :loading="actingOn === r.id && actingAction === 'approve'"
                    class="mr-2"
                    @click="approve(r)"
                  >
                    Approve
                  </base-button>
                  <base-button
                    size="sm"
                    variant="outlined"
                    tone="danger"
                    :disabled="actingOn === r.id"
                    :loading="actingOn === r.id && actingAction === 'deny'"
                    @click="deny(r)"
                  >
                    Deny
                  </base-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          @input="onPageChange"
        ></b-pagination>
      </card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import axios from 'axios';
import swal from 'sweetalert2';
import { Auth } from 'aws-amplify';
import { BPagination } from 'bootstrap-vue';
import { API_LOCATION } from '@/api-config';
import { date, time, formatGroupDate, formatGroupTime } from '@/plugins/dates';
import currencies from '@/assets/currencies.json';
import { formatPhoneDisplay } from '@/util/phoneFormatter';
import TabRail from '@/components/TabRail.vue';
import Badge from '@/components/Badge';

const instance = getCurrentInstance();
const notify = (opts: any) => (instance as any).proxy.$notify(opts);

const group = JSON.parse(localStorage.getItem('group') || '{}');
const companyId = localStorage.getItem('companyId');
const companyGroupId = group.id;
const date_format = group.date_format;
const time_format = group.time_format;
const currency = group.currency;
const number_format = group.number_format;
const currency_format = group.currency_format;

const tabs = [{ name: 'Pending' }, { name: 'Decided' }];
const activeTab = ref(0);
const loading = ref(false);
const requests = ref<any[]>([]);
const pendingCount = ref(0);
const totalRows = ref(0);
const currentPage = ref(1);
const perPage = ref(10);
const perPageOptions = [5, 10, 25, 50];
const actingOn = ref<number | null>(null);
const actingAction = ref<'approve' | 'deny' | null>(null);

const filterValues = ref<any>({
  name: '',
  email: '',
  event: '',
  payment_mode: '',
  outcome: '',
  date: { mode: 'range', days: '', from: '', to: '' },
});
// FilterBar (saved-views enabled) emits exactly one initial `change` after
// resolving precedence; that drives the first fetch instead of a mounted hook.
let didInitialFetch = false;

const filterSchema = computed(() => [
  { key: 'name', type: 'text', label: 'Customer name', placeholder: 'Name', operatorLabel: 'contains', primary: true },
  { key: 'email', type: 'text', label: 'Email', placeholder: 'Email', operatorLabel: 'contains', primary: true },
  { key: 'event', type: 'text', label: 'Event', placeholder: 'Event name', operatorLabel: 'contains', primary: true },
  { key: 'date', type: 'daterange', label: 'Received date', primary: true },
  { key: 'payment_mode', type: 'select', label: 'Payment mode', options: [
    { label: 'Charged at request', value: 'charge_now' },
    { label: 'Charged after approval', value: 'on_approval' },
  ] },
  { key: 'outcome', type: 'select', label: 'Outcome', options: [
    { label: 'Approved', value: 'approved' },
    { label: 'Denied', value: 'denied' },
    { label: 'Expired', value: 'expired' },
    { label: 'Cancelled', value: 'cancelled' },
    { label: 'Abandoned', value: 'abandoned' },
  ] },
]);

function hasPermissions(permission: string) {
  const p = JSON.parse(localStorage.getItem('permissions') || '[]') || [];
  return p.includes(permission);
}

function computeDateRange() {
  const d = filterValues.value.date || {};
  let from = '';
  let to = '';
  if (d.mode === 'days' && d.days !== '' && d.days != null) {
    const today = new Date();
    const dt = new Date();
    dt.setDate(dt.getDate() - Number(d.days));
    const start = dt.getFullYear() + '-' + ('0' + (dt.getMonth() + 1)).slice(-2) + '-' + ('0' + dt.getDate()).slice(-2);
    const end = today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2) + '-' + ('0' + today.getDate()).slice(-2);
    from = new Date(`${start}T00:00:00`).toISOString();
    to = new Date(`${end}T23:59:59`).toISOString();
  } else if (d.mode === 'range' && d.from && d.to) {
    from = new Date(`${d.from}T00:00:00`).toISOString();
    to = new Date(`${d.to}T23:59:59`).toISOString();
  }
  return { from, to };
}

async function getAuthConfig() {
  const session = await Auth.currentSession();
  return { headers: { Authorization: session.getIdToken().getJwtToken() } };
}

function buildListUrl(count: number, offset: number) {
  const { from, to } = computeDateRange();
  const status = activeTab.value === 0 ? 'pending' : 'decided';
  const params = new URLSearchParams({
    status,
    count: String(count),
    offset: String(offset),
    name: filterValues.value.name || '',
    email: filterValues.value.email || '',
    event: filterValues.value.event || '',
    payment_mode: filterValues.value.payment_mode || '',
    outcome: filterValues.value.outcome || '',
    from,
    to,
  });
  return `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/booking-requests?${params.toString()}`;
}

async function fetchRequests(resetPage = false) {
  if (resetPage) currentPage.value = 1;
  loading.value = true;
  try {
    const config = await getAuthConfig();
    const offset = (currentPage.value - 1) * perPage.value;
    const response = await axios.get(buildListUrl(perPage.value, offset), config);
    requests.value = response.data.requests || [];
    totalRows.value = response.data.total_count || 0;
    pendingCount.value = response.data.pending_count || 0;
  } catch (error) {
    console.error('Failed to load booking requests:', error);
    notify({ type: 'danger', message: 'Failed to load requests' });
    requests.value = [];
    totalRows.value = 0;
  } finally {
    loading.value = false;
  }
}

function onFiltersChanged(values: any) {
  filterValues.value = { ...filterValues.value, ...values, date: { ...values.date } };
  if (!didInitialFetch) {
    didInitialFetch = true;
    fetchRequests(false);
  } else {
    fetchRequests(true);
  }
}

function onPerPageChange(n: number) {
  perPage.value = n;
  fetchRequests(true);
}

function onPageChange() {
  fetchRequests(false);
}

function escapeCSVValue(value: any) {
  if (typeof value === 'string') {
    value = value.replace(/"/g, '""');
    if (value.includes(',') || value.includes('\n')) {
      value = `"${value}"`;
    }
  }
  return value;
}

async function downloadCSV() {
  loading.value = true;
  try {
    const config = await getAuthConfig();
    const rows: any[] = [];
    const batch = 500;
    let offset = 0;
    while (offset <= totalRows.value) {
      const response = await axios.get(buildListUrl(batch, offset), config);
      rows.push(...(response.data.requests || []));
      if ((response.data.requests || []).length < batch) break;
      offset += batch;
    }

    let csv = 'Received,Customer Name,Customer Email,Event,Requested Time,Payment Mode,Paid,Due,Status,Decided By,Deny Reason\n';
    rows.forEach((r) => {
      csv += [
        getLocalDateTime(r.created_at),
        escapeCSVValue(customerName(r)),
        escapeCSVValue(r.customer_email || ''),
        escapeCSVValue(eventName(r)),
        r.booking_date ? `${getDate(r.booking_date, date_format)} ${getTime(r.start_time, time_format)}` : '',
        r.payment_capture_mode,
        parseFloat(r.paid || 0).toFixed(2),
        parseFloat(r.due || 0).toFixed(2),
        outcomeLabel(r.status),
        escapeCSVValue(r.decided_by_first_name ? `${r.decided_by_first_name} ${r.decided_by_last_name}` : ''),
        escapeCSVValue(r.deny_reason || ''),
      ].join(',');
      csv += '\n';
    });

    const anchor = document.createElement('a');
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    anchor.target = '_blank';
    anchor.download = 'booking-requests.csv';
    anchor.click();
  } catch (error) {
    console.error('Failed to download booking requests:', error);
    notify({ type: 'danger', message: 'Download failed' });
  } finally {
    loading.value = false;
  }
}

async function approve(r: any) {
  const result = await swal({
    title: 'Approve this booking request?',
    text: 'The slot becomes a confirmed booking and the customer receives a confirmation email.',
    type: 'question',
    showCancelButton: true,
    confirmButtonText: 'Approve',
    cancelButtonText: 'Go back',
  });
  if (!result.value) return;

  actingOn.value = r.id;
  actingAction.value = 'approve';
  try {
    const config = await getAuthConfig();
    const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/booking-requests/${r.id}/approve`;
    const response = await axios.post(url, {}, config);

    if (parseFloat(response.data.due) > 0 && response.data.transaction_id) {
      try {
        const prUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/transactions/${response.data.transaction_id}/payments/request`;
        await axios.post(prUrl, { email: response.data.customer_email, bookings: [] }, config);
      } catch (prError) {
        notify({ type: 'warning', message: 'Request approved. Payment request could not be sent, send it from the transaction page.', timeout: 6000 });
        await fetchRequests();
        return;
      }
    }
    notify({ type: 'success', message: 'Booking request approved' });
    await fetchRequests();
  } catch (error: any) {
    notify({ type: 'danger', message: error?.response?.data?.error || 'The request could not be approved' });
  } finally {
    actingOn.value = null;
    actingAction.value = null;
  }
}

async function deny(r: any) {
  const refundNote = parseFloat(r.paid) > 0
    ? `${getAmount(r.paid)} will be refunded to the customer automatically.`
    : 'No payment has been captured.';
  const result = await swal({
    title: 'Deny this booking request?',
    text: `The slot will be released. ${refundNote} You can add a reason that is shared with the customer.`,
    input: 'textarea',
    inputPlaceholder: 'Reason (optional)',
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Deny request',
    cancelButtonText: 'Go back',
  });
  if (result.dismiss) return;

  actingOn.value = r.id;
  actingAction.value = 'deny';
  try {
    const config = await getAuthConfig();
    const url = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/booking-requests/${r.id}/deny`;
    await axios.post(url, { deny_reason: result.value ? String(result.value).slice(0, 500) : null }, config);
    notify({ type: 'success', message: 'Booking request denied' });
    await fetchRequests();
  } catch (error: any) {
    notify({ type: 'danger', message: error?.response?.data?.error || 'The request could not be denied' });
  } finally {
    actingOn.value = null;
    actingAction.value = null;
  }
}

function formatPhone(phone: string, country: string) {
  return formatPhoneDisplay(phone, country || 'US');
}

function customerName(r: any) {
  const name = [r.first_name, r.last_name].filter(Boolean).join(' ');
  return name || r.customer_email || 'Unknown customer';
}

function eventName(r: any) {
  return r.game_internal_name || r.game_name || r.form_game_name || 'Any event';
}

function outcomeLabel(status: string) {
  const labels: Record<string, string> = {
    pending: 'Pending',
    approved: 'Approved',
    denied: 'Denied',
    expired: 'Expired',
    cancelled: 'Cancelled',
    abandoned: 'Abandoned',
    denying: 'Denying',
  };
  return labels[status] || status;
}

function outcomeBadgeType(status: string) {
  if (status === 'approved') return 'success';
  if (status === 'denied' || status === 'abandoned') return 'danger';
  if (status === 'expired') return 'warning';
  if (status === 'denying') return 'info';
  return 'default';
}

function getDate(d: string, fmt: string) {
  return date(d, fmt);
}
function getTime(d: string, fmt: string) {
  return time(d, fmt);
}
function getLocalDateTime(d: string) {
  if (!d) return '';
  const dt = formatGroupDate(d, { dayOfWeek: false });
  const tm = formatGroupTime(d);
  if (!dt && !tm) return '';
  return `${dt}, ${tm}`;
}

function getAmount(value: any) {
  if (value == null) return '';
  const currencyInfo = (currencies as any).currencies.find((c: any) => c.code === currency);
  const symbol = currencyInfo ? currencyInfo.symbol : '$';
  const isPostfix = currencyInfo ? currencyInfo.postfix : false;

  let formattedAmount;
  if (number_format === 1) {
    formattedAmount = parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
  } else {
    formattedAmount = parseFloat(value).toFixed(2);
  }

  if (currency_format === 0) {
    return isPostfix ? formattedAmount + symbol : symbol + formattedAmount;
  }
  return formattedAmount + ' ' + currency;
}

// A saved view carries the active tab as page-owned extra state. When a view
// restores the tab, the following `change` event does the fetch — the tab
// watcher must not double-fetch (or fetch before the filters have applied).
let restoringTab = false;
function onRestoreExtra(extra: any) {
  if (extra && typeof extra.tab === 'number' && extra.tab !== activeTab.value) {
    restoringTab = true;
    activeTab.value = extra.tab;
  }
}

watch(activeTab, () => {
  if (restoringTab) {
    restoringTab = false;
    return;
  }
  fetchRequests(true);
});

// ---------------------------------------------------------------------------
// Real-time updates. Same websocket the booking calendar uses, but connected
// WITHOUT a date param: the broadcast lambda treats a NULL-date connection as
// subscribed to every date in the group, which is what an inbox needs.
// Broadcasts arrive from autoblocking-module (request/booking creation) and
// the approve/deny lambdas (decisions, incl. cron expiries).
const WS_URL = 'wss://3bqa7hkquf.execute-api.us-east-1.amazonaws.com/production';
let ws: WebSocket | null = null;
let wsHeartbeat: ReturnType<typeof setInterval> | null = null;
let wsRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let wsReconnectAttempts = 0;
const WS_MAX_RECONNECT = 8;

async function initRequestsWebSocket() {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const params = new URLSearchParams({ token, companyGroupId: String(companyGroupId) });
    ws = new WebSocket(`${WS_URL}?${params.toString()}`);

    ws.onopen = () => {
      const wasReconnecting = wsReconnectAttempts > 0;
      wsReconnectAttempts = 0;
      startHeartbeat();
      // Resync after a reconnect to catch events missed while disconnected.
      if (wasReconnecting) fetchRequests(false);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'pong') return;
        if (message.type === 'booking-changed'
          && parseInt(message.companyGroupId) === parseInt(String(companyGroupId))) {
          // Debounce: bursts (multi-slot changes) collapse into one refetch.
          if (wsRefreshTimer) clearTimeout(wsRefreshTimer);
          wsRefreshTimer = setTimeout(() => fetchRequests(false), 500);
        }
      } catch (e) {
        console.error('Error processing WebSocket message:', e);
      }
    };

    ws.onclose = (event) => {
      stopHeartbeat();
      if (event.code !== 1000) scheduleReconnect();
    };

    ws.onerror = (e) => console.error('WebSocket error:', e);
  } catch (e) {
    console.error('Failed to initialize WebSocket:', e);
    scheduleReconnect();
  }
}

function scheduleReconnect() {
  if (wsReconnectAttempts >= WS_MAX_RECONNECT) return;
  wsReconnectAttempts++;
  const delay = Math.min(1000 * Math.pow(2, wsReconnectAttempts - 1), 30000);
  setTimeout(() => initRequestsWebSocket(), delay);
}

function startHeartbeat() {
  // Ping every 4 minutes, well inside the gateway's 10-minute idle timeout.
  wsHeartbeat = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ action: 'ping' })); } catch (e) { console.error('Heartbeat failed:', e); }
    }
  }, 4 * 60 * 1000);
}

function stopHeartbeat() {
  if (wsHeartbeat) { clearInterval(wsHeartbeat); wsHeartbeat = null; }
}

onMounted(() => initRequestsWebSocket());
onBeforeUnmount(() => {
  if (wsRefreshTimer) clearTimeout(wsRefreshTimer);
  if (ws) {
    stopHeartbeat();
    ws.close(1000, 'Component closing');
    ws = null;
  }
});
</script>

<style lang="scss" scoped>
// Row links keep the plain text look and reveal themselves on hover, matching
// the transaction id links on the Transactions page.
.request-row-link {
  text-decoration: none;

  // Slanted "opens elsewhere" arrow, same treatment as the transaction id
  // links on the Transactions page.
  &__arrow {
    color: #e14eca;
    font-size: 11px;
    margin-left: 4px;
    transition: opacity 0.15s ease;
  }

  &:hover {
    color: #e14eca !important;
    text-decoration: underline;

    .request-row-link__arrow {
      opacity: 0.7;
    }
  }
}
</style>
